import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const SectionKicker = ({ children }) => (
  <p
    className="text-xs uppercase tracking-[0.25em] font-medium mb-5"
    style={{ color: '#D4820A' }}
  >
    {children}
  </p>
);

const SectionHeading = ({ children, light }) => (
  <h2
    className="text-3xl md:text-4xl font-normal tracking-tight mb-6"
    style={{
      fontFamily: 'Cormorant Garamond, serif',
      color: light ? '#FFFFFF' : '#0F2942'
    }}
  >
    {children}
  </h2>
);

const Paragraph = ({ children, light }) => (
  <p
    className="text-base md:text-lg leading-relaxed"
    style={{ color: light ? 'rgba(240, 235, 226, 0.72)' : '#0F2942' }}
  >
    {children}
  </p>
);

const AboutPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />

      <SEO
        title="About CultureCareers"
        path="/about"
        description="A curated platform for careers and opportunities in the cultural economy, built to bring structure, visibility, and access to India's creative ecosystem."
      />

      {/* 1. Hero */}
      <section
        className="px-6 md:px-12 lg:px-24 py-20 md:py-28"
        style={{ backgroundColor: '#0F2942' }}
        data-testid="about-hero"
      >
        <div className="max-w-3xl">
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-none mb-6 text-white"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            About CultureCareers
          </h1>
          <div className="w-20 h-px mb-8" style={{ backgroundColor: 'rgba(240, 235, 226, 0.35)' }} />
          <p
            className="text-lg md:text-xl font-light leading-relaxed"
            style={{ color: 'rgba(240, 235, 226, 0.75)' }}
          >
            A curated platform for careers and opportunities in the cultural economy.
          </p>
        </div>
      </section>

      {/* 2. Why we exist — editorial manifesto */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" data-testid="about-why">
        <div className="max-w-3xl">
          <SectionKicker>About CultureCareers</SectionKicker>
          <h2
            className="font-normal tracking-tight mb-12"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: '#0F2942',
              fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.01em'
            }}
            data-testid="about-manifesto-headline"
          >
            Culture is not a hobby.{' '}
            <span style={{ color: '#D4820A', fontStyle: 'italic' }}>It is a career.</span>
          </h2>

          <div className="space-y-6">
            <Paragraph>
              Yet across India, the cultural sector continues to be treated as informal, inaccessible, and secondary. Opportunities circulate through closed networks. Roles are filled through word of mouth. Emerging talent struggles to find a way in, while organisations struggle to find the right people.
            </Paragraph>
            <Paragraph>
              There is no central place to discover work in the arts. And more importantly, there is little understanding of how hiring in this sector actually works.
            </Paragraph>

            {/* Italic emphasis trio */}
            <div
              className="border-l-2 pl-6 my-10 space-y-2"
              style={{ borderColor: '#D4820A' }}
            >
              <p
                className="text-lg md:text-xl"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic',
                  color: '#0F2942'
                }}
              >
                Hiring a curator is not the same as hiring a consultant.
              </p>
              <p
                className="text-lg md:text-xl"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic',
                  color: '#0F2942'
                }}
              >
                Building a festival team is not the same as filling corporate roles.
              </p>
              <p
                className="text-lg md:text-xl"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic',
                  color: '#0F2942'
                }}
              >
                Creative work requires nuance, context, and a deep understanding of practice.
              </p>
            </div>

            <Paragraph>
              CultureCareers was built to bridge this gap.
            </Paragraph>
          </div>

          {/* Closing manifesto lines */}
          <div className="mt-10 space-y-2">
            <p
              className="text-xl md:text-2xl"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
            >
              We bring <strong style={{ fontWeight: 600 }}>structure</strong> to an unstructured ecosystem.
            </p>
            <p
              className="text-xl md:text-2xl"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
            >
              <strong style={{ fontWeight: 600 }}>Visibility</strong> to hidden opportunities.
            </p>
            <p
              className="text-xl md:text-2xl"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
            >
              And <strong style={{ fontWeight: 600 }}>access</strong> to careers that are often out of reach.
            </p>
          </div>
        </div>
      </section>

      <div className="mx-6 md:mx-12 lg:mx-24" style={{ borderTop: '1px solid #F0EBE2' }} />

      {/* 2.5 Cinematic field band — between manifesto and "What we do" */}
      <section
        className="px-6 md:px-12 lg:px-24 py-20 md:py-28"
        style={{ backgroundColor: '#0F2942' }}
        data-testid="about-field-video"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: '#D4820A' }}>
              In the field
            </p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-6 text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              This is what the work looks like.
            </h2>
            <div className="w-14 h-px mb-7" style={{ backgroundColor: 'rgba(212,130,10,0.5)' }} />
            <p className="text-base md:text-lg leading-relaxed font-light mb-5" style={{ color: 'rgba(240, 235, 226, 0.72)' }}>
              Galleries opening. Festivals running. Curators talking to artists. Conservators studying objects. Producers setting up sound checks.
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: 'rgba(240, 235, 226, 0.72)', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}>
              When we say cultural careers, this is what we mean. Real people. Real practice. Real work.
            </p>
          </div>
          <div className="flex justify-center md:justify-end">
            <div
              className="relative overflow-hidden rounded-xl shadow-2xl w-full max-w-[360px]"
              style={{ aspectRatio: '9/16', backgroundColor: '#0F2942' }}
              aria-hidden="true"
            >
              <video
                src="/media/sizzle-hero.mp4"
                poster="/media/poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ aspectRatio: '9/16' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. What we do — three service pillars */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" data-testid="about-what-we-do">
        <div className="max-w-6xl">
          <SectionKicker>What we do</SectionKicker>
          <SectionHeading>Three ways we support the cultural sector.</SectionHeading>
          <p className="text-base md:text-lg leading-relaxed max-w-2xl mb-14" style={{ color: '#0F2942' }}>
            Whether you&rsquo;re hiring, building your brand, or growing your own practice, CultureCareers is designed to help.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            {[
              {
                kicker: '01 / Recruitment',
                title: 'Recruitment & Talent Placement',
                body:
                  "We find, vet and place creative professionals in full-time, contract and freelance roles across India's arts and cultural sector. Curators, designers, producers, marketers, conservators. If the role is in culture, we can fill it.",
                cta: 'Hire through us',
                to: '/post-job',
                testid: 'pillar-hire'
              },
              {
                kicker: '02 / Creative Services',
                title: 'Creative Services for Organisations',
                body:
                  "Galleries and cultural organisations need marketing support they can't always afford to hire in-house. We offer retainer-based content, communications, and audience-building services tailored to the cultural sector.",
                cta: 'Work with us',
                to: '/services',
                testid: 'pillar-work'
              },
              {
                kicker: '03 / Artist Support',
                title: 'Artist & Career Support',
                body:
                  'Artist statements, grant applications, residency proposals, portfolio reviews. We help creative professionals present their work and access opportunities with the credibility they deserve.',
                cta: 'Get support',
                to: '/services',
                testid: 'pillar-artist'
              }
            ].map((p) => (
              <Link
                key={p.testid}
                to={p.to}
                className="group block rounded-xl p-8 md:p-9 transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #F0EBE2'
                }}
                data-testid={p.testid}
              >
                <p
                  className="text-[11px] uppercase tracking-[0.2em] font-medium mb-5"
                  style={{ color: '#D4820A' }}
                >
                  {p.kicker}
                </p>
                <h3
                  className="text-2xl md:text-[26px] font-normal tracking-tight mb-4 leading-snug"
                  style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    color: '#0F2942'
                  }}
                >
                  {p.title}
                </h3>
                <p
                  className="text-sm md:text-[15px] leading-relaxed mb-7"
                  style={{ color: '#0F2942' }}
                >
                  {p.body}
                </p>
                <span
                  className="inline-flex items-center text-sm font-medium transition-colors group-hover:underline"
                  style={{ color: '#D4820A' }}
                >
                  {p.cta} <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>

          {/* Closing pull-quote */}
          <div className="mt-16 md:mt-20 max-w-3xl" data-testid="about-pillars-quote">
            <div className="border-l-2 pl-6 md:pl-8" style={{ borderColor: '#D4820A' }}>
              <p
                className="font-bold tracking-tight"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: '#0F2942',
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  lineHeight: 1.25,
                  letterSpacing: '-0.01em'
                }}
              >
                We don&rsquo;t just list opportunities. We understand how careers in culture{' '}
                <span style={{ color: '#D4820A', fontStyle: 'italic' }}>actually</span> work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Our approach — dark editorial band */}
      <section
        className="px-6 md:px-12 lg:px-24 py-20 md:py-28"
        style={{ backgroundColor: '#0F2942' }}
        data-testid="about-approach"
      >
        <div className="max-w-3xl">
          <p
            className="text-xs uppercase tracking-[0.25em] font-medium mb-5"
            style={{ color: '#D4820A' }}
          >
            Our approach
          </p>
          <SectionHeading light>We are not a mass job board.</SectionHeading>
          <div className="space-y-6 mb-10">
            <Paragraph light>
              CultureCareers is built on curation, clarity, and relevance.
            </Paragraph>
            <Paragraph light>We focus on:</Paragraph>
          </div>

          <ul className="space-y-5">
            {[
              'Meaningful opportunities',
              'Thoughtful matching',
              'A growing network of creatives and organisations'
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-4 pb-5"
                style={{ borderBottom: '1px solid rgba(240, 235, 226, 0.08)' }}
              >
                <span
                  className="text-sm font-medium mt-2"
                  style={{ color: '#D4820A', minWidth: '1.5rem' }}
                  aria-hidden="true"
                >
                  &bull;
                </span>
                <span className="text-lg md:text-xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {item}
                </span>
              </li>
            ))}
          </ul>

          <p className="mt-10 text-base md:text-lg" style={{ color: 'rgba(240, 235, 226, 0.72)' }}>
            Our goal is not volume. It&rsquo;s quality and access.
          </p>
        </div>
      </section>

      {/* Full-bleed editorial photo */}
      <figure className="relative" data-testid="about-bleed-photo">
        <img
          src="https://images.unsplash.com/photo-1719400030798-af7ac5760172?w=1800&q=70&auto=format&fit=crop"
          alt="A group of people gathered on the floor in front of a mural. Photo by Neon Wang on Unsplash"
          loading="lazy"
          decoding="async"
          width="1800"
          height="900"
          className="w-full h-[300px] md:h-[440px] object-cover"
        />
      </figure>

      {/* 5. Who it's for */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" data-testid="about-who-for">
        <div className="max-w-5xl">
          <SectionKicker>Who it&rsquo;s for</SectionKicker>
          <SectionHeading>Built for both sides of the creative economy.</SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 mt-10">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: '#0F2942' }}>
                For talent
              </p>
              <Paragraph>
                Artists, designers, writers, curators, performers, and cultural professionals looking to build meaningful careers.
              </Paragraph>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: '#0F2942' }}>
                For organisations
              </p>
              <Paragraph>
                Museums, galleries, studios, foundations, and creative businesses looking to hire, collaborate, and grow.
              </Paragraph>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-6 md:mx-12 lg:mx-24" style={{ borderTop: '1px solid #F0EBE2' }} />

      {/* 6. Beyond listings */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" data-testid="about-services">
        <div className="max-w-3xl">
          <SectionKicker>Beyond listings</SectionKicker>
          <SectionHeading>Support across the cultural sector.</SectionHeading>
          <Paragraph>
            In addition to opportunities, CultureCareers offers support across:
          </Paragraph>

          <ul className="mt-8 space-y-4">
            {[
              'Hiring and talent sourcing',
              'Artist positioning and portfolio guidance',
              'Residency and grant applications',
              'Creative and cultural consulting'
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-4 pb-4"
                style={{ borderBottom: '1px solid #F0EBE2' }}
              >
                <span className="text-sm font-medium mt-2" style={{ color: '#D4820A', minWidth: '1.5rem' }} aria-hidden="true">
                  &bull;
                </span>
                <span className="text-base md:text-lg" style={{ color: '#0F2942' }}>
                  {item}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-10">
            <Link
              to="/services"
              className="inline-flex items-center text-sm font-medium hover:underline"
              style={{ color: '#0F2942' }}
              data-testid="about-services-link"
            >
              Learn more about our services <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. Vision — quiet, centered, editorial */}
      <section
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
        style={{ backgroundColor: '#F0EBE2' }}
        data-testid="about-vision"
      >
        <div className="max-w-3xl mx-auto text-center">
          <SectionKicker>Our vision</SectionKicker>
          <h2
            className="text-3xl md:text-5xl font-light tracking-tight leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
          >
            To build India&rsquo;s most trusted network for careers and opportunities in the cultural economy.
          </h2>
        </div>
      </section>

      {/* 8. CTA */}
      <section
        className="px-6 md:px-12 lg:px-24 py-20 md:py-28"
        style={{ backgroundColor: '#0F2942' }}
        data-testid="about-cta"
      >
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: '#D4820A' }}>
            Get involved
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal tracking-tight mb-6 text-white"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Whether you&rsquo;re looking for opportunities, hiring talent, or building within the cultural sector, CultureCareers is designed to support you.
          </h2>
          <div className="w-16 h-px mx-auto mb-10" style={{ backgroundColor: 'rgba(212,130,10,0.5)' }} />

          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/browse"
              className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all hover:opacity-90"
              style={{ backgroundColor: '#D4820A', color: '#0F2942' }}
              data-testid="about-cta-explore"
            >
              Explore opportunities <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Link>
            <Link
              to="/talent"
              className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all border hover:bg-white/10"
              style={{ borderColor: 'rgba(240, 235, 226, 0.35)', color: '#FFFFFF' }}
              data-testid="about-cta-talent"
            >
              Join the network
            </Link>
            <Link
              to="/post-job"
              className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all border hover:bg-white/10"
              style={{ borderColor: 'rgba(240, 235, 226, 0.35)', color: '#FFFFFF' }}
              data-testid="about-cta-post"
            >
              Post an opportunity
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
